/**
 * Environment detection utilities for Analytics and Logger
 * Centralized to avoid duplication and circular dependencies
 */

import { GAME_METADATAS } from "@mental-mint/shared-types";
import { ENVIRONMENTS } from "./AnalyticsConstants";

/**
 * Get environment from hostname - checks against actual game URLs
 * Used by both Analytics and Logger for consistent environment detection
 */
export const getEnvironmentFromHostname = (hostname: string): string => {
  // Normalize hostname by removing www. prefix if present
  const normalizedHostname = hostname.replace(/^www\./, '');
  
  // Check if hostname matches any production game URL
  const productionDomains = Object.values(GAME_METADATAS).map(metadata => {
    try {
      const url = new URL(metadata.gameURL);
      // Also normalize production domains to remove www.
      return url.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }).filter(Boolean);
  
  // Add landing page domain to production domains
  productionDomains.push('mentalmintgames.com');
  
  // console.debug("[EnvironmentUtils] Checking hostname:", normalizedHostname);
  // console.debug("[EnvironmentUtils] Production domains:", productionDomains);
  
  if (productionDomains.includes(normalizedHostname)) {
    return ENVIRONMENTS.PRODUCTION;
  }
  
  // Default to development for localhost, staging, dev, or any unrecognized domain
  return ENVIRONMENTS.DEVELOPMENT;
};

/**
 * Get the current environment
 * Safe to use in both browser and SSR contexts
 */
export const getEnvironment = (): string => {
  if (typeof window !== 'undefined') {
    return getEnvironmentFromHostname(window.location.hostname);
  }
  return ENVIRONMENTS.DEVELOPMENT; // Default for SSR/Node environments
};
